import { useMemo } from 'react';

import type { TtqSession } from '../../services/ttqService';
import { TtqNativeCanvas } from './TtqNativeCanvas';
import type { TtqDerivedProvenanceView } from './ttqibDerivedStack';
import {
  TTQIB_BIRDS_EYE_EXIT_ZOOM_THRESHOLD,
  TTQIB_BIRDS_EYE_ZOOM_THRESHOLD,
  TTQ_SESSION_OVERLAY_TOP_CLASS,
  normalizeViewport,
} from './ttqibViewport';
import type { TtqibNativeCanvasModel, TtqibViewport } from './types';

type TtqUnifiedCanvasProps = {
  session: TtqSession;
  canvasModel: TtqibNativeCanvasModel | null;
  /** 由 useTtqCanvasLodMode 推导，带 zoom 迟滞 */
  birdsEyeMode: boolean;
  viewport?: TtqibViewport;
  viewportInitialized?: boolean;
  onViewportChange: (viewport: TtqibViewport) => void;
  onMoveParticle?: (particleId: string, position: { x: number; y: number }) => void;
  onSwitchLayer?: (layerId: string) => void;
  onSelectObject?: (objectId: string | null) => void;
  selectedObjectIds?: string[];
  selectedSegmentObjectIds?: string[];
  onBehaviorSelectionChange?: (particleIds: string[]) => void;
  onToggleSegment?: (item: { objectId: string; text: string; particleId: string }) => void;
  onOpenPinTi?: (particleId: string) => void;
  onOpenLadderPractice?: (particleId: string) => void;
  showLadderPractice?: boolean;
  onClearSegmentTray?: () => void;
  tastedAnchorsByParticleId?: Record<string, string[]>;
  chatAnchorsByParticleId?: Record<string, import('../../utils/aiChatSegmentKey').AiChatSegmentAnchor[]>;
  onOpenChatThread?: (threadId: string) => void;
  derivedProvenanceByParticleId?: Record<string, TtqDerivedProvenanceView>;
  derivedParticleIds?: string[];
  onDeleteDerivedParticle?: (particleId: string) => void;
  onRegenerateDerivedParticle?: (particleId: string) => void;
  regeneratingDerivedId?: string | null;
  onMeasureParticle?: (particleId: string, size: { width: number; height: number }) => void;
  linkedRefsByParticleId?: Record<string, Array<{ particleId: string; title: string }>>;
  onFocusLinkedParticle?: (particleId: string) => void;
  onCenterParticle?: (particleId: string) => void;
  birdsSelectedIds: string[];
  onBirdsSelectedChange: (ids: string[]) => void;
  cttqAggregating?: boolean;
  cttqMemberPickTargetId?: string | null;
  onStartCttqMemberPick?: (convergeParticleId: string) => void;
  onCancelCttqMemberPick?: () => void;
  onPickCttqMember?: (convergeParticleId: string, memberParticleId: string) => void;
  focusParticleId?: string | null;
  onFocusParticleHandled?: () => void;
  onEnterBirdsEyeZoom?: () => void;
  onEnterDeriveZoom?: () => void;
  pinnedFloats?: Array<{ particleId: string; left: number; top: number }>;
  onPinnedFloatsChange?: (floats: Array<{ particleId: string; left: number; top: number }>) => void;
  convergeRelayoutNonce?: number;
};

/**
 * 统一品题画布：始终同一 Native world。
 * 状态 A（缩小）→ 衍生标号 + 收敛摘要卡；
 * 状态 B（放大）→ 衍生整卡 + 收敛标号。
 * 相对位置不变；缩放锚定一对多卡坐标系。
 */
export function TtqUnifiedCanvas({
  session,
  canvasModel,
  birdsEyeMode,
  viewport,
  viewportInitialized,
  onViewportChange,
  onMoveParticle,
  onSwitchLayer,
  onSelectObject,
  selectedObjectIds = [],
  selectedSegmentObjectIds = [],
  onBehaviorSelectionChange,
  onToggleSegment,
  onOpenPinTi,
  onOpenLadderPractice,
  showLadderPractice = false,
  onClearSegmentTray,
  tastedAnchorsByParticleId = {},
  chatAnchorsByParticleId = {},
  onOpenChatThread,
  derivedProvenanceByParticleId = {},
  derivedParticleIds = [],
  onDeleteDerivedParticle,
  onRegenerateDerivedParticle,
  regeneratingDerivedId = null,
  onMeasureParticle,
  linkedRefsByParticleId = {},
  onFocusLinkedParticle,
  onCenterParticle,
  birdsSelectedIds,
  onBirdsSelectedChange,
  cttqAggregating = false,
  cttqMemberPickTargetId = null,
  onStartCttqMemberPick,
  onCancelCttqMemberPick,
  onPickCttqMember,
  focusParticleId = null,
  onFocusParticleHandled,
  onEnterBirdsEyeZoom,
  onEnterDeriveZoom: _onEnterDeriveZoom,
  pinnedFloats = [],
  onPinnedFloatsChange,
  convergeRelayoutNonce = 0,
}: TtqUnifiedCanvasProps) {
  const vp = normalizeViewport(viewport ?? canvasModel?.state.viewport);
  const modeLabel = useMemo(
    () => (birdsEyeMode ? '多对一 · 关系层' : '一对多 · 衍生'),
    [birdsEyeMode],
  );

  return (
    <>
      <div className={`pointer-events-none absolute left-4 z-30 ${TTQ_SESSION_OVERLAY_TOP_CLASS}`}>
        <span className="pointer-events-none inline-flex items-center rounded-lg border border-border/80 bg-surface/90 px-2 py-1 text-[10px] text-muted shadow-sm">
          {modeLabel}
          <span className="ml-1.5 text-[9px] opacity-70">
            {birdsEyeMode
              ? `放大至 ≥${Math.round(TTQIB_BIRDS_EYE_EXIT_ZOOM_THRESHOLD * 100)}% 显示整卡`
              : `缩小至 ≤${Math.round(TTQIB_BIRDS_EYE_ZOOM_THRESHOLD * 100)}% 显示收敛摘要`}
          </span>
        </span>
      </div>
      <TtqNativeCanvas
        session={session}
        canvasModel={canvasModel}
        birdsEyeMode={birdsEyeMode}
        viewport={vp}
        viewportInitialized={viewportInitialized}
        onViewportChange={onViewportChange}
        onMoveParticle={onMoveParticle}
        onSwitchLayer={onSwitchLayer}
        onSelectObject={onSelectObject}
        selectedObjectIds={selectedObjectIds}
        selectedSegmentObjectIds={selectedSegmentObjectIds}
        onBehaviorSelectionChange={onBehaviorSelectionChange}
        onToggleSegment={onToggleSegment}
        onOpenPinTi={onOpenPinTi}
        onOpenLadderPractice={onOpenLadderPractice}
        showLadderPractice={showLadderPractice}
        onClearSegmentTray={onClearSegmentTray}
        tastedAnchorsByParticleId={tastedAnchorsByParticleId}
        chatAnchorsByParticleId={chatAnchorsByParticleId}
        onOpenChatThread={onOpenChatThread}
        derivedProvenanceByParticleId={derivedProvenanceByParticleId}
        derivedParticleIds={derivedParticleIds}
        onDeleteDerivedParticle={onDeleteDerivedParticle}
        onRegenerateDerivedParticle={onRegenerateDerivedParticle}
        regeneratingDerivedId={regeneratingDerivedId}
        onMeasureParticle={onMeasureParticle}
        linkedRefsByParticleId={linkedRefsByParticleId}
        onFocusLinkedParticle={onFocusLinkedParticle}
        onCenterParticle={onCenterParticle}
        onEnterBirdsEyeZoom={onEnterBirdsEyeZoom}
        pinnedFloats={pinnedFloats}
        onPinnedFloatsChange={onPinnedFloatsChange}
        birdsSelectedIds={birdsSelectedIds}
        onBirdsSelectedChange={onBirdsSelectedChange}
        cttqAggregating={cttqAggregating}
        cttqMemberPickTargetId={cttqMemberPickTargetId}
        onStartCttqMemberPick={onStartCttqMemberPick}
        onCancelCttqMemberPick={onCancelCttqMemberPick}
        onPickCttqMember={onPickCttqMember}
        focusParticleId={focusParticleId}
        onFocusParticleHandled={onFocusParticleHandled}
        convergeRelayoutNonce={convergeRelayoutNonce}
      />
    </>
  );
}
